import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SiteHeader from '../components/common/SiteHeader';
import Breadcrumb from '../components/common/Breadcrumb';
import GallerySizeControl from '../components/common/GallerySizeControl';
import VideoModal from '../components/common/VideoModal';
import AddVideoModal from '../components/common/AddVideoModal';
import { useAuth } from '../hooks/useAuth';
import useGallerySize from '../hooks/useGallerySize';
import galleryService from '../services/galleryService';
import { getYouTubeThumbnailUrl } from '../utils/youtubeUtils';
import { areAllVideosYouTube } from '../utils/galleryUtils';
import { formatDuration } from '../utils/durationFormatter';

function GalleryPage() {
	const { galleryName } = useParams();
	const { user } = useAuth();
	const { size, setSize, sizes, mobileSizes, allSizeConfigs, getGridClass } = useGallerySize();
	const [gallery, setGallery] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [search, setSearch] = useState('');
	const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
	const [showAddVideoModal, setShowAddVideoModal] = useState(false);
	const [saving, setSaving] = useState(false);
	const [successMessage, setSuccessMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

	// Check if current user can edit this gallery
	const canUserEdit = () => {
		if (!user || !gallery) return false;

		// Owner can always edit
		if (user.id === gallery.gallery.ownerId) return true;

		// Check if user's email is in invitations with canEdit permission
		if (user.email && gallery.gallery.invitations) {
			return gallery.gallery.invitations.some(
				inv => inv.email.toLowerCase() === user.email.toLowerCase() && inv.canEdit
			);
		}

		return false;
	};

	useEffect(() => {
		loadGallery();
	}, [galleryName]); // eslint-disable-line react-hooks/exhaustive-deps

	const loadGallery = async () => {
		try {
			setLoading(true);
			const galleryData = await galleryService.getGalleryByUrlName(galleryName);
			setGallery(galleryData);
			setError(null);
		} catch (err) {
			setError('Gallery not found or could not be loaded.');
		} finally {
			setLoading(false);
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
				<SiteHeader />
				<div className="p-8 max-w-6xl mx-auto w-full">
					<div className="text-center py-12">
						<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500 mx-auto"></div>
						<p className="mt-4 text-gray-600">Loading gallery...</p>
					</div>
				</div>
			</div>
		);
	}

	if (error || !gallery) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
				<SiteHeader />
				<div className="p-8 max-w-6xl mx-auto w-full">
					<div className="bg-white rounded-lg shadow-sm p-8">
						<div className="text-center">
							<svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<h1 className="text-2xl font-bold text-gray-800 mb-2">Gallery Not Found</h1>
							<p className="text-gray-600 mb-6">{error || 'The gallery you are looking for could not be found.'}</p>
							<button
								onClick={loadGallery}
								className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium transition-colors flex items-center gap-2"
							>
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
								</svg>
								Try Again
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const filteredVideos = gallery.videos.filter(v =>
		(v.description || '').toLowerCase().includes(search.toLowerCase()) ||
		(v.title || '').toLowerCase().includes(search.toLowerCase())
	);

	// Check if all videos in the gallery are from YouTube
	const isYouTubeOnly = areAllVideosYouTube(filteredVideos);

	const handleVideoClick = (index) => {
		setSelectedVideoIndex(index);
	};

	const handleCloseModal = () => {
		setSelectedVideoIndex(null);
	};

	const handleNextVideo = () => {
		if (selectedVideoIndex < filteredVideos.length - 1) {
			setSelectedVideoIndex(selectedVideoIndex + 1);
		}
	};

	const handlePreviousVideo = () => {
		if (selectedVideoIndex > 0) {
			setSelectedVideoIndex(selectedVideoIndex - 1);
		}
	};

	const handleAddVideo = async (newVideo) => {
		setSaving(true);
		setErrorMessage('');
		setSuccessMessage('');

		try {
			// Prepare the new video object
			const newVideoObj = {
				url: newVideo.url,
				title: newVideo.title,
				description: newVideo.description || '',
				thumbnailUrl: newVideo.thumbnailUrl || ''
			};

			// Map existing videos
			const existingVideos = gallery.videos.map(v => ({
				url: v.url,
				title: v.title,
				description: v.description || '',
				thumbnailUrl: v.thumbnailUrl || ''
			}));

			// Add new video at the beginning or end based on gallery settings
			const updatedVideos = gallery.gallery.addNewVideosAtBeginning
				? [newVideoObj, ...existingVideos]
				: [...existingVideos, newVideoObj];

			const updateData = {
				name: gallery.gallery.name,
				description: gallery.gallery.description || '',
				urlName: gallery.gallery.urlName,
				isPrivate: gallery.gallery.isPrivate,
				addNewVideosAtBeginning: gallery.gallery.addNewVideosAtBeginning,
				videoLinks: updatedVideos,
				invitedEmails: gallery.invitedEmails || [],
				invitations: gallery.gallery.invitations || []
			};

			await galleryService.updateGallery(gallery.gallery.id, updateData);

			// Show success message briefly
			setSuccessMessage('Video added successfully!');
			setTimeout(() => setSuccessMessage(''), 3000);

			// Reload the gallery to show the new video
			await loadGallery();
		} catch (err) {
			setErrorMessage(err.message || 'Failed to add video. Please try again.');
			setTimeout(() => setErrorMessage(''), 5000);
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteVideo = async (videoId) => {
		if (!window.confirm('Are you sure you want to delete this video?')) {
			return;
		}

		setSaving(true);
		setErrorMessage('');
		setSuccessMessage('');

		try {
			// Remove the video from the list
			const updatedVideos = gallery.videos
				.filter(v => v.id !== videoId)
				.map(v => ({
					url: v.url,
					title: v.title,
					description: v.description || '',
					thumbnailUrl: v.thumbnailUrl || ''
				}));

			const updateData = {
				name: gallery.gallery.name,
				description: gallery.gallery.description || '',
				urlName: gallery.gallery.urlName,
				isPrivate: gallery.gallery.isPrivate,
				addNewVideosAtBeginning: gallery.gallery.addNewVideosAtBeginning,
				videoLinks: updatedVideos,
				invitedEmails: gallery.gallery.invitedEmails || [],
				invitations: gallery.gallery.invitations || []
			};

			await galleryService.updateGallery(gallery.gallery.id, updateData);

			setSuccessMessage('Video deleted successfully!');
			setTimeout(() => setSuccessMessage(''), 3000);

			// Reload the gallery
			await loadGallery();
		} catch (err) {
			setErrorMessage(err.message || 'Failed to delete video. Please try again.');
			setTimeout(() => setErrorMessage(''), 5000);
		} finally {
			setSaving(false);
		}
	};

	const handleShareGallery = async () => {
		try {
			const galleryUrl = window.location.href;
			await navigator.clipboard.writeText(galleryUrl);
			setShowCopiedTooltip(true);
			setTimeout(() => setShowCopiedTooltip(false), 2000);
		} catch (err) {
			setErrorMessage('Failed to copy link. Please try again.');
			setTimeout(() => setErrorMessage(''), 3000);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
			<SiteHeader />
			<div className="p-4 md:p-8 max-w-6xl mx-auto w-full">
				<Breadcrumb items={[
					{ label: 'My galleries', href: '/dashboard' },
					{ label: gallery.gallery.name }
				]} />

				{/* Gallery Header */}
				<div className="mt-6 bg-white rounded-lg shadow-sm p-6">
					{/* Success/Error Messages */}
					{successMessage && (
						<div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
							<svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
								<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
							</svg>
							<span>{successMessage}</span>
						</div>
					)}
					{errorMessage && (
						<div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
							{errorMessage}
						</div>
					)}

					<div className="flex items-start justify-between">
						<div className="flex-1">
							<h1 className="text-3xl font-bold text-gray-900 mb-2">{gallery.gallery.name}</h1>
							{/* Show "Shared by" indicator for galleries not owned by current user */}
							{user && gallery.gallery.ownerId !== user.id && gallery.gallery.ownerName && (
								<div className="text-sm text-gray-500 mb-2">
									Shared by {gallery.gallery.ownerName}
								</div>
							)}
							{gallery.gallery.description && (
								<p className="text-gray-600 text-lg">{gallery.gallery.description}</p>
							)}
							<div className="mt-4 flex items-center gap-3 text-sm text-gray-500">
								{gallery.gallery.isPrivate && (
									<span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md font-medium text-xs">
										Invite only
									</span>
								)}
								{!gallery.gallery.isPrivate && (
									<div className="flex items-center gap-2">
										<span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md font-medium text-xs">
											Anyone with link
										</span>
										<button
											onClick={handleShareGallery}
											className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors text-xs font-medium"
											title={showCopiedTooltip ? "Copied!" : "Copy gallery link"}
										>
											{showCopiedTooltip ? (
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											) : (
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.658 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
												</svg>
											)}
											<span>{showCopiedTooltip ? "Copied!" : "Copy link"}</span>
										</button>
									</div>
								)}
							</div>
						</div>
						{/* Action buttons */}
						<div className="ml-2 md:ml-4 flex items-center gap-1 md:gap-2">
							{/* Show Add Video button if user can edit */}
							{user && canUserEdit() && (
								<>
									<button
										onClick={() => setShowAddVideoModal(true)}
										disabled={saving}
										className="px-2 py-1.5 md:px-3 md:py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium transition-colors flex items-center gap-1 md:gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
										title="Add video to gallery"
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
										</svg>
										<span className="hidden sm:inline">Add Video</span>
										<span className="sm:hidden">Add</span>
									</button>
									{/* Show Settings button only for owner */}
									{user.id === gallery.gallery.ownerId && (
										<Link
											to={`/manage/${gallery.gallery.id}`}
											className="p-2 md:p-3 rounded-full hover:bg-gray-100 transition-colors group/icon"
											title="Manage gallery settings"
										>
											<svg className="w-5 h-5 md:w-6 md:h-6 text-gray-600 group-hover/icon:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
											</svg>
										</Link>
									)}
								</>
							)}
						</div>
					</div>

					{/* Search Bar and Size Control */}
					{gallery.videos.length > 0 && (
						<div className="mt-6 pt-6 border-t">
							<div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
								<div className="relative flex-1">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
										</svg>
									</div>
									<input
										type="text"
										placeholder="Search videos..."
										value={search}
										onChange={e => setSearch(e.target.value)}
										className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
									/>
									{search && (
										<button
											onClick={() => setSearch('')}
											className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-700 text-gray-400 transition-colors"
											title="Clear search"
										>
											<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
											</svg>
										</button>
									)}
								</div>
								<GallerySizeControl
									size={size}
									onSizeChange={setSize}
									sizes={sizes}
									mobileSizes={mobileSizes}
									sizeConfigs={allSizeConfigs}
								/>
							</div>
						</div>
					)}
				</div>

				{/* Videos Grid */}
				<div className="mt-6">
					{gallery.videos.length === 0 ? (
						<div className="bg-white rounded-lg shadow-sm p-12">
							<div className="text-center">
								<svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
								</svg>
								<h3 className="text-lg font-medium text-gray-900 mb-2">No videos yet</h3>
								<p className="text-gray-600">This gallery doesn't have any videos at the moment.</p>
							</div>
						</div>
					) : filteredVideos.length === 0 ? (
						<div className="bg-white rounded-lg shadow-sm p-12">
							<div className="text-center">
								<svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
								</svg>
								<h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
								<p className="text-gray-600">Try adjusting your search terms</p>
							</div>
						</div>
					) : (
						<div className={`grid ${getGridClass()} gap-4 transition-all duration-300`}>
							{filteredVideos.map((video, index) => {
								const thumbnailUrl = video.thumbnailUrl || getYouTubeThumbnailUrl(video.url, 'hqdefault');
								const isEditable = user && canUserEdit();

								return (
									<div
										key={video.id}
										className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-all group relative"
									>
										{/* Delete button overlay (only for editors) */}
										{isEditable && (
											<div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
												<button
													onClick={(e) => {
														e.stopPropagation();
														handleDeleteVideo(video.id);
													}}
													className="p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
													title="Delete video"
												>
													<svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
													</svg>
												</button>
											</div>
										)}

										{/* Thumbnail - clickable to open video modal */}
										<div
											onClick={() => handleVideoClick(index)}
											className="cursor-pointer"
										>
											<div className="relative aspect-video bg-gray-200 group-hover:opacity-95 transition-opacity">
												{thumbnailUrl ? (
													<img
														src={thumbnailUrl}
														alt={video.title || video.description || 'Video thumbnail'}
														className="w-full h-full object-cover"
													/>
												) : (
													<div className="w-full h-full flex items-center justify-center text-gray-400">
														<svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
															<path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
														</svg>
													</div>
												)}

												{/* Duration Badge */}
												{video.duration && (
													<div className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white px-2 py-1 rounded text-xs font-semibold z-10">
														{formatDuration(video.duration)}
													</div>
												)}

												{/* Play button overlay */}
												<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
													<div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all">
														<svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
															<path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
														</svg>
													</div>
												</div>
											</div>

											{/* Video Info */}
											<div className="p-3">
												<h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1" title={video.title || video.description}>
													{video.title || video.description || 'Untitled Video'}
												</h3>

												{/* Channel Title */}
												{video.channelTitle && (
													<p className="text-xs text-gray-500 mb-1">
														{video.channelTitle}
													</p>
												)}

												{video.description && video.title && (
													<p
														className="text-xs text-gray-600 line-clamp-1 sm:line-clamp-2 leading-relaxed"
														title={video.description}
													>
														{video.description}
													</p>
												)}
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>

			{/* Video Modal */}
			{selectedVideoIndex !== null && (
				<VideoModal
					video={filteredVideos[selectedVideoIndex]}
					onClose={handleCloseModal}
					onNext={handleNextVideo}
					onPrevious={handlePreviousVideo}
					hasNext={selectedVideoIndex < filteredVideos.length - 1}
					hasPrevious={selectedVideoIndex > 0}
					enableYouTubeAutoPlay={isYouTubeOnly}
				/>
			)}

			{/* Add Video Modal */}
			<AddVideoModal
				isOpen={showAddVideoModal}
				onClose={() => setShowAddVideoModal(false)}
				onAdd={handleAddVideo}
			/>
		</div>
	);
}

export default GalleryPage;
